import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { resetPassword } from "../services/authService";
import { useTranslation } from "react-i18next";

/* انیمیشن دکمه (هم‌نام با LoginPage تا یکسان باشد) */
const forgotStyles = `
@keyframes raya-shine {
  0%   { transform: translateX(120%) skewX(-20deg); }
  100% { transform: translateX(-220%) skewX(-20deg); }
}
@keyframes raya-gradient {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
.raya-cta { background-size: 200% 200%; animation: raya-gradient 6s ease infinite; }
.raya-cta .raya-shine {
  position: absolute; inset: 0; width: 40%; pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
  animation: raya-shine 3.2s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .raya-cta, .raya-cta .raya-shine { animation: none !important; }
}
`;

const labelCls = "block text-sm font-medium mb-1.5 text-[#1c4747] dark:text-teal-100/90";

const inputCls =
    "w-full h-[3.25rem] rounded-[.9rem] pl-12 pr-4 text-base outline-none transition-all duration-200 " +
    "border border-[#1c4747]/20 bg-white/75 text-[#0f2e2e] placeholder:text-sm placeholder:text-[#1c4747]/45 " +
    "focus:bg-white focus:border-[#e0ad3a] focus:ring-4 focus:ring-[#e0ad3a]/25 " +
    "dark:border-white/15 dark:bg-white/[0.06] dark:text-[#f7f3e6] dark:placeholder:text-white/40 " +
    "dark:focus:!bg-white/10 dark:focus:!border-[#f2c14e] dark:focus:!ring-[#f2c14e]/20";

const eyeBtnCls =
    "absolute inset-y-0 left-2 my-auto h-9 w-9 flex items-center justify-center rounded-lg " +
    "text-[#1c4747]/60 hover:text-[#1c4747] hover:bg-[#1c4747]/10 " +
    "dark:text-white/50 dark:hover:text-[#f2c14e] dark:hover:bg-white/10 transition-colors";

const ctaCls =
    "raya-cta relative overflow-hidden w-full h-[3.25rem] rounded-[.9rem] text-white font-semibold " +
    "bg-gradient-to-r from-[#1c4747] via-[#2a7a76] to-[#1c4747] " +
    "shadow-[0_10px_24px_-8px_rgba(28,71,71,.65)] hover:shadow-[0_14px_30px_-8px_rgba(28,71,71,.8)] " +
    "hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 " +
    "flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#e0ad3a]/50 " +
    "disabled:opacity-70 disabled:cursor-not-allowed " +
    "dark:from-[#e0ad3a] dark:via-[#f6d070] dark:to-[#e0ad3a] dark:text-[#0d2f2f] " +
    "dark:shadow-[0_10px_24px_-8px_rgba(242,193,78,.55)]";

const linkBtnCls =
    "mt-1 w-full text-center text-sm font-bold text-[#b8860b] hover:text-[#8a6508] " +
    "dark:text-[#f2c14e] dark:hover:text-[#ffe08a] transition-colors py-1.5";

export default function ForgotPassword() {
    const { t } = useTranslation(["user"]);

    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [confirmShowPassword, setConfirmShowPassword] = useState(false);
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setValue,
    } = useForm();

    const filterPassword = (v) =>
        v.replace(/[^A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g, "");

    const handlePasswordChange = (e) => {
        const filteredValue = filterPassword(e.target.value);
        setPassword(filteredValue);
        setValue("password", filteredValue);
    };

    const handleConfirmPasswordChange = (e) => {
        const filteredValue = filterPassword(e.target.value);
        setConfirmPassword(filteredValue);
        setValue("confirmPassword", filteredValue);
    };

    const onSubmit = async (data) => {
        setLoading(true);

        try {
            const payload = {
                password: data.password,
                confirmPassword: data.confirmPassword,
            };
            const res = await resetPassword(payload);
            if (res.ok) {
                toast.success(t("forgotPassword.successToast"), { containerId: "main-toast" });
                setLoading(false);
                navigate("/user/dashboard");
            } else {
                toast.error(res.data?.message || t("forgotPassword.errorToast"), { containerId: "main-toast" });
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    // نکته: پوسته (پس‌زمینه، پنل لوگو، کارت شیشه‌ای) و ToastContainer را Auth.jsx می‌سازد.
    return (
        <div dir="rtl" className="w-full">
            <style>{forgotStyles}</style>

            <h1 className="text-xl sm:text-2xl font-bold mb-1.5 text-[#1c4747] dark:text-white">
                {t("forgotPassword.title")}
            </h1>
            <p className="text-sm mb-6 text-[#1c4747]/70 dark:text-teal-100/70">
                {t("forgotPassword.subtitle")}
            </p>

            <div className="min-h-[300px]">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="on">
                    <div>
                        <label className={labelCls}>{t("forgotPassword.passwordLabel")}</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                {...register("password")}
                                onChange={handlePasswordChange}
                                placeholder={t("forgotPassword.passwordPlaceholder")}
                                autoComplete="new-password"
                                className={inputCls}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={
                                    showPassword
                                        ? t("forgotPassword.hidePassword")
                                        : t("forgotPassword.showPassword")
                                }
                                className={eyeBtnCls}
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className={labelCls}>{t("forgotPassword.confirmPasswordLabel")}</label>
                        <div className="relative">
                            <input
                                type={confirmShowPassword ? "text" : "password"}
                                value={confirmPassword}
                                {...register("confirmPassword")}
                                onChange={handleConfirmPasswordChange}
                                placeholder={t("forgotPassword.confirmPasswordPlaceholder")}
                                autoComplete="new-password"
                                className={inputCls}
                            />
                            <button
                                type="button"
                                onClick={() => setConfirmShowPassword((prev) => !prev)}
                                aria-label={
                                    confirmShowPassword
                                        ? t("forgotPassword.hidePassword")
                                        : t("forgotPassword.showPassword")
                                }
                                className={eyeBtnCls}
                                tabIndex={-1}
                            >
                                {confirmShowPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    <button type="submit" disabled={loading} className={ctaCls}>
                        {!loading && <span className="raya-shine" aria-hidden="true" />}
                        <span className="relative flex items-center gap-2">
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin w-4 h-4" />
                                    {t("forgotPassword.submitting")}
                                </>
                            ) : (
                                t("forgotPassword.submit")
                            )}
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={() => (window.location.href = "/login")}
                        className={linkBtnCls}
                    >
                        {t("forgotPassword.backToLogin")}
                    </button>
                </form>
            </div>
        </div>
    );
}   